import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const HOVER_CLOSE_DELAY_MS = 300;

/**
 * Open/close behaviour for the TrancheStatus panel.
 * Hover opens it and closes it after a short delay; a click pins it open
 * until the badge is clicked again, the user clicks outside, or presses Esc.
 */
const useTranchePanel = (defaultOpen) => {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(defaultOpen);
  const closeTimer = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();
  const [triggerPosition, setTriggerPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const isOpen = hoverOpen || clickOpen;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const startClose = useCallback(() => {
    closeTimer.current = setTimeout(
      () => setHoverOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  }, []);

  const handleMouseEnter = useCallback(() => {
    cancelClose();
    setHoverOpen(true);
  }, [cancelClose]);

  const close = useCallback(() => {
    setClickOpen(false);
    setHoverOpen(false);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      setTriggerPosition(buttonRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  // Flyout is fixed to the window, so follow the badge while the page
  // scrolls or resizes, and close once the badge leaves the window rather
  // than leave the panel floating over unrelated content.
  useEffect(() => {
    if (!isOpen) return undefined;
    const update = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      if (
        rect.bottom < 0 ||
        rect.top > window.innerHeight ||
        rect.right < 0 ||
        rect.left > window.innerWidth
      ) {
        close();
        return;
      }
      setTriggerPosition(rect);
    };
    // Capture phase also catches scrolling inside page containers.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, close]);

  // Move focus into the panel only when the user pinned it open, so
  // keyboard and screen-reader users land on the content. Hover does not
  // move focus. preventScroll stops a jump before Flyout positions the panel.
  useEffect(() => {
    if (clickOpen) panelRef.current?.focus({ preventScroll: true });
  }, [clickOpen]);

  const handleBadgeClick = () => {
    if (clickOpen) {
      close();
    } else {
      setClickOpen(true);
    }
  };

  // Flyout treats the badge as "outside" the panel, so ignore those clicks
  // or they undo the badge's own click handler.
  const handleOutsideClick = (e) => {
    if (buttonRef.current?.contains(e.target)) return;
    close();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      close();
      buttonRef.current?.focus();
    }
  };

  return {
    isOpen,
    triggerPosition,
    handleOutsideClick,
    buttonProps: {
      ref: buttonRef,
      'aria-expanded': isOpen,
      'aria-controls': isOpen ? panelId : undefined,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: startClose,
      onClick: handleBadgeClick,
      onKeyDown: handleKeyDown,
    },
    panelProps: {
      id: panelId,
      ref: panelRef,
      tabIndex: -1,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: startClose,
      onKeyDown: handleKeyDown,
    },
  };
};

export default useTranchePanel;
